import { useState, useEffect, useRef } from "react";
import { subscribeToPushNotifications } from "./utils/pushSubscription";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import Messages from "@/pages/Messages";
import Tournament from "@/pages/Tournament";
import CommunityPosts from "@/pages/CommunityPosts";
import Events from "@/pages/Events";
import Map from "@/pages/Map";
import Battle from "@/pages/Battle";
import Store from "@/pages/Store";
import { Toaster } from "@/components/ui/sonner";
import { BattleModeProvider, useBattleMode } from "@/contexts/BattleModeContext";
import { BACKEND_URL } from "@/API_CONFIG";

export const API = `${BACKEND_URL}/api`;

// Set global axios timeout to prevent hanging requests
axios.defaults.timeout = 30000; // 30 seconds timeout for all requests

function AppContent() {
  const { isBattleMode } = useBattleMode();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMessages, setShowMessages] = useState(false);
  const [showTournament, setShowTournament] = useState(false);
  const [showCommunityPosts, setShowCommunityPosts] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showBattle, setShowBattle] = useState(false);
  const [showStore, setShowStore] = useState(false);
  const [error, setError] = useState(null);
  const [showRecoveryButton, setShowRecoveryButton] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(Date.now());
  
  // Swipe detection refs
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const lastSwipeTime = useRef(0);

  // Debug logging for unreadCount changes
  useEffect(() => {
    console.log(`[App.js] 🔔 unreadCount state changed: ${unreadCount}`);
    console.log(`[App.js] Current page - Messages: ${showMessages}, Tournament: ${showTournament}, Community: ${showCommunityPosts}, Events: ${showEvents}`);
  }, [unreadCount, showMessages, showTournament, showCommunityPosts, showEvents]);

  // Global WebSocket AND polling for real-time unread count updates (SAME as Messages page)
  // DISABLED IN BATTLE MODE
  useEffect(() => {
    if (!user?.id) return;
    
    // 🎮 BATTLE MODE: Skip all polling and websocket connections
    if (isBattleMode) {
      console.log('[App.js] ⚔️ BATTLE MODE: Skipping WebSocket and polling setup');
      return;
    }

    console.log('[App.js] 🔌 Setting up global WebSocket and polling for message notifications...');

    // Fetch initial unread count
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/chat/list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const count = response.data.total_unread || 0;
        console.log('[App.js] 💬 Fetched unread count:', count);
        setUnreadCount(count);
        
        // Force a small state change to trigger re-render
        setRefreshTrigger(Date.now());
        
        // Update app badge
        if ('setAppBadge' in navigator) {
          if (count > 0) {
            navigator.setAppBadge(count).catch(err => console.log('Badge API error:', err));
          } else {
            navigator.clearAppBadge().catch(err => console.log('Badge API error:', err));
          }
        }
      } catch (error) {
        console.error('[App.js] Error fetching unread count:', error);
      }
    };

    fetchUnreadCount();
    
    // Set up polling every 3 seconds (backup for WebSocket, SAME as Messages.js)
    const pollInterval = setInterval(() => {
      console.log('[App.js] 🔄 Polling for unread count...');
      fetchUnreadCount();
    }, 3000);
    console.log('[App.js] ✅ Polling started - checking every 3 seconds');

    // Set up WebSocket for real-time message notifications (EXACT same as Messages.js)
    const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://').replace('/api', '')}/ws/${user.id}`;
    console.log('[App.js] 🔌 Connecting to WebSocket:', wsUrl);
    const websocket = new WebSocket(wsUrl);

    websocket.onopen = () => {
      console.log('[App.js] ✅ WebSocket connected - real-time message notifications active');
    };

    websocket.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      console.log('[App.js] 📨 WebSocket message received:', data);

      // Handle incoming message (SAME logic as Messages.js lines 81-150)
      if (data.chat_id && data.sender_id) {
        const isFromOtherUser = data.sender_id !== user.id;
        
        if (isFromOtherUser) {
          console.log('[App.js] ✅ New message from another user detected');
          console.log('[App.js] 🔔 Updating app badge...');
          
          // Fetch accurate unread count from server (SAME as Messages.js does)
          // Backend already incremented unread_counts before broadcasting message
          try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/chat/list`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const unreadCount = response.data.total_unread || 0;
            console.log(`[App.js] 💬 Updated unread count from server: ${unreadCount}`);
            
            // Update state
            setUnreadCount(unreadCount);
            
            // Force component refresh to ensure badges appear immediately
            setRefreshTrigger(Date.now());
            console.log('[App.js] 🔄 Triggered component refresh');
            
            // Update app badge
            if ('setAppBadge' in navigator) {
              if (unreadCount > 0) {
                await navigator.setAppBadge(unreadCount);
                console.log(`[App.js] ✅ App badge updated to ${unreadCount}`);
              } else {
                await navigator.clearAppBadge();
                console.log('[App.js] ✅ App badge cleared (no unread messages)');
              }
            }
          } catch (error) {
            console.error('[App.js] ❌ Error updating app badge:', error);
          }
        }
      }
    };

    websocket.onerror = (error) => {
      console.error('[App.js] ❌ WebSocket error:', error);
    };

    websocket.onclose = () => {
      console.log('[App.js] 🔌 WebSocket disconnected');
    };

    return () => {
      console.log('[App.js] 🔌 Cleaning up WebSocket connection and polling');
      websocket.close();
      clearInterval(pollInterval);
    };
  }, [user, isBattleMode]);

  useEffect(() => {
    // 🎮 BATTLE MODE: Skip recovery checks to prevent cache operations
    if (isBattleMode) {
      console.log('[App.js] ⚔️ BATTLE MODE: Skipping recovery/cache checks');
      return;
    }
    
    // Detect if app is stuck from previous crash/RAM clear
    const appStartTime = Date.now();
    const lastSuccessfulLoad = localStorage.getItem('last_successful_load');
    
    if (lastSuccessfulLoad) {
      const timeSinceLastLoad = appStartTime - parseInt(lastSuccessfulLoad);
      // If last successful load was less than 5 seconds ago, might be in reload loop
      if (timeSinceLastLoad < 5000) {
        console.warn('[Recovery] App reloading too frequently, clearing caches...');
        // Clear service worker caches
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => caches.delete(name));
          });
        }
      }
    }
    
    // Set a timeout to show recovery button if app doesn't load in 10 seconds
    const loadTimeout = setTimeout(() => {
      if (loading) {
        console.warn('[Recovery] App stuck loading, showing recovery option');
        setShowRecoveryButton(true);
        setLoading(false);
      }
    }, 10000);
    
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Cache token for service worker background updates
      if ('caches' in window) {
        const cacheVersion = 'v2-' + new Date().toISOString().split('T')[0];
        caches.open('beypointer-runtime-' + cacheVersion)
          .then(cache => {
            return cache.put('/auth-token', new Response(JSON.stringify({ token }), {
              headers: { 'Content-Type': 'application/json' }
            }));
          })
          .catch(err => console.log('Failed to cache token:', err));
      }
      
      fetchUserData().finally(() => clearTimeout(loadTimeout));
      
      // Subscribe to push notifications for background delivery
      // 🎮 BATTLE MODE: Skip push notification setup
      if (!isBattleMode) {
        const setupPush = async () => {
          console.log('[App] Setting up push notifications...');
          const success = await subscribeToPushNotifications();
          if (success) {
            console.log('✅ Push notifications enabled - badges will update in background');
          } else {
            console.log('⚠️ Push notifications not available - badges only update when app is open');
            // Retry once after 5 seconds if failed
            setTimeout(async () => {
              console.log('[App] Retrying push subscription...');
              const retrySuccess = await subscribeToPushNotifications();
              if (retrySuccess) {
                console.log('✅ Push notifications enabled on retry');
              }
            }, 5000);
          }
        };
        setupPush();
      } else {
        console.log('[App.js] ⚔️ BATTLE MODE: Skipping push notification setup');
      }
      
      // Auto-refresh token every 7 days to keep session alive indefinitely
      const refreshToken = async () => {
        try {
          const response = await axios.post(`${API}/auth/refresh-token`);
          const newToken = response.data.access_token;
          localStorage.setItem('token', newToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          console.log('Token refreshed successfully');
        } catch (error) {
          console.error('Token refresh failed:', error);
          // If refresh fails with 401, token is truly invalid
          if (error.response?.status === 401) {
            handleLogout();
          }
        }
      };
      
      // Refresh immediately on app start (if token is old)
      const tokenAge = localStorage.getItem('token_timestamp');
      const daysSinceRefresh = tokenAge ? (Date.now() - parseInt(tokenAge)) / (1000 * 60 * 60 * 24) : 999;
      
      if (daysSinceRefresh > 7) {
        refreshToken();
        localStorage.setItem('token_timestamp', Date.now().toString());
      }
      
      // Set up periodic refresh every 7 days
      const refreshInterval = setInterval(() => {
        refreshToken();
        localStorage.setItem('token_timestamp', Date.now().toString());
      }, 7 * 24 * 60 * 60 * 1000); // 7 days
      
      return () => clearInterval(refreshInterval);
    } else {
      setLoading(false);
      clearTimeout(loadTimeout);
    }
    
    return () => clearTimeout(loadTimeout);
    
    // Handle app coming back from background
    // 🎮 BATTLE MODE: Disable visibility change handlers
    const handleVisibilityChange = async () => {
      if (isBattleMode) {
        console.log('[App.js] ⚔️ BATTLE MODE: Ignoring visibility change');
        return;
      }
      
      if (!document.hidden && user) {
        console.log('App came back to foreground, refreshing user data...');
        fetchUserData().catch(err => {
          console.error('Failed to refresh user data:', err);
          // Don't force logout - keep session persistent
          // User must manually logout
        });
      } else if (document.hidden && user) {
        // App went to background - update badge with current unread count
        console.log('[Badge] App went to background, updating badge...');
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/chat/list`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const unreadCount = response.data.total_unread || 0;
          
          // Update app badge
          if ('setAppBadge' in navigator) {
            if (unreadCount > 0) {
              await navigator.setAppBadge(unreadCount);
              console.log('[Badge] Badge set to:', unreadCount);
            } else {
              await navigator.clearAppBadge();
              console.log('[Badge] Badge cleared');
            }
          }
        } catch (err) {
          console.error('[Badge] Failed to update badge on background:', err);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Handle network changes (especially for PWA)
    // 🎮 BATTLE MODE: Disable network handlers
    const handleOnline = () => {
      if (isBattleMode) {
        console.log('[App.js] ⚔️ BATTLE MODE: Ignoring online event');
        return;
      }
      
      console.log('[Network] Back online');
      // Refresh user data when connection restored
      if (user) {
        fetchUserData(true).catch(err => {
          console.error('[Network] Failed to refresh after reconnection:', err);
        });
      }
      // Clear any error states
      setError(null);
    };
    
    const handleOffline = () => {
      if (isBattleMode) {
        console.log('[App.js] ⚔️ BATTLE MODE: Ignoring offline event');
        return;
      }
      
      console.log('[Network] Offline');
      setError('No internet connection. App will sync when back online.');
      // Clear error after 3 seconds
      setTimeout(() => setError(null), 3000);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Check connection status on mount
    if (!navigator.onLine && user) {
      handleOffline();
    }
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user, isBattleMode]);

  // Periodic background refresh of chats when user is logged in
  // DISABLED IN BATTLE MODE
  useEffect(() => {
    if (!user) return;
    
    // 🎮 BATTLE MODE: Skip background chat refreshing
    if (isBattleMode) {
      console.log('[App.js] ⚔️ BATTLE MODE: Skipping background chat refresh');
      return;
    }

    // Refresh chats every 30 seconds in background
    const intervalId = setInterval(() => {
      console.log('🔄 Auto-refreshing chats in background...');
      preloadChatsInBackground(user.id);
    }, 30000); // 30 seconds

    return () => clearInterval(intervalId);
  }, [user, isBattleMode]);

  const fetchUserData = async (silent = false) => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      
      // Only update user if data has actually changed (avoid unnecessary re-renders)
      setUser(prevUser => {
        const newUser = response.data;
        // Check if user data is actually different
        if (prevUser && JSON.stringify(prevUser) === JSON.stringify(newUser)) {
          return prevUser; // Return same object to prevent re-render
        }
        return newUser;
      });
      
      // Only preload chats on initial fetch, not on every refresh
      if (!silent) {
        preloadChatsInBackground(response.data.id);
      }
      
      // Mark successful load
      localStorage.setItem('last_successful_load', Date.now().toString());
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Only logout on 401 (unauthorized) - network errors don't logout
      // This keeps user logged in even with temporary connectivity issues
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
      }
      // For other errors (network issues, 500, etc), keep user logged in
    } finally {
      setLoading(false);
    }
  };

  // Preload chats in background for instant messages view
  const preloadChatsInBackground = async (userId) => {
    try {
      console.log('🔄 Preloading chats in background...');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chat/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Cache chats to localStorage for instant display when user opens Messages
      localStorage.setItem(`chats_${userId}`, JSON.stringify(response.data));
      console.log('✅ Chats preloaded and cached successfully');
      
      // Also preload messages for the 3 most recent chats
      const allChats = [...response.data.direct_chats, ...response.data.group_chats]
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        .slice(0, 3);
      
      for (const chat of allChats) {
        try {
          const messagesResponse = await axios.get(`${API}/chat/${chat.id}/messages`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          localStorage.setItem(`messages_${chat.id}`, JSON.stringify(messagesResponse.data.messages));
          console.log(`✅ Preloaded messages for chat: ${chat.id}`);
        } catch (err) {
          console.error(`Error preloading messages for chat ${chat.id}:`, err);
        }
      }
    } catch (error) {
      console.error('Error preloading chats:', error);
      // Silent fail - don't disrupt user experience
    }
  };

  const handleLogin = async (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('token_timestamp', Date.now().toString());
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Cache token for service worker to use
    if ('caches' in window) {
      try {
        const cache = await caches.open('beypointer-runtime-' + 'v2-' + new Date().toISOString().split('T')[0]);
        await cache.put('/auth-token', new Response(JSON.stringify({ token }), {
          headers: { 'Content-Type': 'application/json' }
        }));
      } catch (err) {
        console.log('Failed to cache token for SW:', err);
      }
    }
    
    setUser(userData);
  };

  const handleLogout = async () => {
    // Notify backend of logout to mark user as offline
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout notification failed:', error);
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('token_timestamp');
    localStorage.removeItem('accountStatus');
    localStorage.removeItem('stadiumPreferences');
    localStorage.removeItem('leaderboardCache');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setShowMessages(false);
    setShowTournament(false);
    setShowCommunityPosts(false);
    setShowEvents(false);
    setShowMap(false);
    setShowBattle(false);
  };
  
  const safeNavigate = (navigationFn) => {
    try {
      setError(null);
      navigationFn();
    } catch (error) {
      console.error('Navigation error:', error);
      setError(error.message);
      // Reset to dashboard on error
      setShowMessages(false);
      setShowTournament(false);
      setShowCommunityPosts(false);
      setShowEvents(false);
      setShowMap(false);
    }
  };

  const [messageUsername, setMessageUsername] = useState(null);

  const handleOpenMessages = (username = null) => {
    // Clear unread count when opening messages
    console.log('[App.js] 📭 Opening messages - clearing unread count');
    setUnreadCount(0);
    
    // Clear app badge
    if ('clearAppBadge' in navigator) {
      navigator.clearAppBadge().catch(err => {
        console.log('Badge API not supported:', err);
      });
    }
    
    // Set username to search for if provided
    if (username) {
      setMessageUsername(username);
    }
    
    // Close all other pages including Map
    setShowMap(false);
    setShowTournament(false);
    setShowCommunityPosts(false);
    setShowEvents(false);
    
    safeNavigate(() => setShowMessages(true));
  };

  const handleOpenCommunityPosts = async () => {
    // Mark community as viewed when user opens it
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/community/mark-viewed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error marking community as viewed:', error);
    }
    
    safeNavigate(() => setShowCommunityPosts(true));
  };
  
  // Global error handler
  useEffect(() => {
    const handleError = (event) => {
      console.error('Global error caught:', event.error);
      // Immediately return to dashboard without delay
      setShowMessages(false);
      setShowTournament(false);
      setShowCommunityPosts(false);
      setShowEvents(false);
      setShowMap(false);
      setError(null);
    };
    
    window.addEventListener('error', handleError);
    
    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);

  // Swipe detection for navigation (except on Dashboard/Home Screen and Battle Mode)
  useEffect(() => {
    const isOnDashboard = !showMessages && !showTournament && !showCommunityPosts && !showEvents && !showMap && !showBattle && !showStore;
    
    // Only enable swipe detection when NOT on dashboard
    if (isOnDashboard) {
      return;
    }
    
    // 🎮 BATTLE MODE: Disable swipe-to-go-back during battles
    // Users swipe on touchpads to control Beyblades, accidental swipes would exit the battle
    if (isBattleMode) {
      console.log('[App.js] ⚔️ BATTLE MODE: Swipe-to-go-back DISABLED to prevent accidental exits');
      return;
    }
    
    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    };
    
    const handleTouchEnd = (e) => {
      if (touchStartX.current === null || touchStartY.current === null) {
        return;
      }
      
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      
      const deltaX = touchEndX - touchStartX.current;
      const deltaY = touchEndY - touchStartY.current;
      
      // Calculate distances
      const horizontalDistance = Math.abs(deltaX);
      const verticalDistance = Math.abs(deltaY);
      
      // Convert half inch to pixels (assuming 96 DPI, 0.5 inch = 48 pixels)
      const minSwipeDistance = 48;
      
      // Check if horizontal swipe (horizontal movement greater than vertical)
      if (horizontalDistance > verticalDistance && horizontalDistance >= minSwipeDistance) {
        // Check cooldown (1 second = 1000ms)
        const now = Date.now();
        if (now - lastSwipeTime.current < 1000) {
          console.log('⏱️ Swipe cooldown active, ignoring swipe');
          touchStartX.current = null;
          touchStartY.current = null;
          return;
        }
        
        // Update last swipe time
        lastSwipeTime.current = now;
        
        // Trigger back navigation
        console.log('👈 Swipe detected, navigating back');
        if (showMessages) {
          setShowMessages(false);
        } else if (showTournament) {
          setShowTournament(false);
        } else if (showCommunityPosts) {
          setShowCommunityPosts(false);
        } else if (showEvents) {
          setShowEvents(false);
        } else if (showMap) {
          setShowMap(false);
        } else if (showBattle) {
          setShowBattle(false);
        } else if (showStore) {
          setShowStore(false);
        }
      }
      
      // Reset touch positions
      touchStartX.current = null;
      touchStartY.current = null;
    };
    
    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [showMessages, showTournament, showCommunityPosts, showEvents, showMap, showBattle, showStore, isBattleMode]);


  if (loading && !showRecoveryButton) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="text-cyan-400 text-xl">Loading...</div>
      </div>
    );
  }
  
  if (showRecoveryButton) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-950 p-4">
        <div className="text-center">
          <div className="text-cyan-400 text-2xl font-bold mb-4">BEYPOINTER</div>
          <div className="text-slate-400 mb-6">
            App is taking longer than expected to load.
            <br />
            This may be due to network issues.
          </div>
          <button
            onClick={() => {
              console.log('[Recovery] User initiated recovery');
              const token = localStorage.getItem('token');
              const tokenTimestamp = localStorage.getItem('token_timestamp');
              
              // Clear localStorage except token
              localStorage.clear();
              if (token) {
                localStorage.setItem('token', token);
                if (tokenTimestamp) {
                  localStorage.setItem('token_timestamp', tokenTimestamp);
                }
              }
              
              // Clear caches
              if ('caches' in window) {
                caches.keys().then(names => {
                  names.forEach(name => caches.delete(name));
                });
              }
              
              // Reload
              window.location.reload();
            }}
            className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold mb-4"
          >
            Clear Cache & Retry
          </button>
          <div>
            <button
              onClick={() => window.location.reload()}
              className="text-slate-500 hover:text-slate-300 text-sm underline"
            >
              Just Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-950 p-4">
        <div className="text-red-400 text-xl mb-4">⚠️ Error</div>
        <div className="text-slate-300 text-center mb-4">{error}</div>
        <button
          onClick={() => {
            setError(null);
            setShowMessages(false);
            setShowTournament(false);
            setShowCommunityPosts(false);
            setShowEvents(false);
            setShowMap(false);
          }}
          className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="App">
      {/* 🎮 BATTLE MODE: Unmount all other components to stop their background processes */}
      
      {/* Dashboard - loads leaderboard and unread counts */}
      {user && !showMessages && !showTournament && !showCommunityPosts && !showEvents && !showMap && !showBattle && !showStore && (
        <Dashboard 
          key="dashboard"
          user={user} 
          onLogout={handleLogout} 
          onUpdateUser={fetchUserData}
          onOpenMessages={handleOpenMessages}
          onOpenTournament={() => safeNavigate(() => setShowTournament(true))}
          onOpenCommunityPosts={handleOpenCommunityPosts}
          onOpenEvents={() => safeNavigate(() => setShowEvents(true))}
          onOpenMap={() => safeNavigate(() => setShowMap(true))}
          onOpenBattle={() => safeNavigate(() => setShowBattle(true))}
          onOpenStore={() => safeNavigate(() => setShowStore(true))}
          unreadCount={unreadCount}
          setUnreadCount={setUnreadCount}
        />
      )}

      {/* Map - loads nearby players */}
      {user && showMap && !isBattleMode && (
        <Map
          key="map"
          user={user}
          onBack={() => safeNavigate(() => setShowMap(false))}
          onOpenMessages={handleOpenMessages}
        />
      )}

      {/* Battle - interactive beyblade stadium */}
      {user && showBattle && (
        <Battle
          key="battle"
          user={user}
          onBack={() => safeNavigate(() => setShowBattle(false))}
          onUpdateUser={fetchUserData}
          onOpenStore={() => {
            setShowBattle(false);
            safeNavigate(() => setShowStore(true));
          }}
        />
      )}
      
      {/* Messages - loads chats and friends list */}
      {user && showMessages && !isBattleMode && (
        <Messages 
          key="messages"
          user={user} 
          onBack={() => {
            safeNavigate(() => setShowMessages(false));
            setMessageUsername(null);
          }} 
          onUpdateUser={fetchUserData}
          searchUsername={messageUsername}
          clearSearchUsername={() => setMessageUsername(null)}
        />
      )}
      
      {/* Tournament - loads tournament brackets in background */}
      {user && showTournament && !isBattleMode && (
        <Tournament 
          key="tournament"
          user={user} 
          onBack={() => safeNavigate(() => setShowTournament(false))}
        />
      )}
      
      {/* Community Posts - loads posts list and new posts status */}
      {user && showCommunityPosts && !isBattleMode && (
        <CommunityPosts 
          key="community"
          user={user} 
          onBack={() => safeNavigate(() => setShowCommunityPosts(false))}
        />
      )}
      
      {/* Events - loads events list and event chats */}
      {user && showEvents && !isBattleMode && (
        <Events 
          key="events"
          user={user} 
          onBack={() => safeNavigate(() => setShowEvents(false))}
        />
      )}

      {/* Store page */}
      {user && showStore && !isBattleMode && (
        <Store 
          user={user}
          onBack={() => safeNavigate(() => setShowStore(false))}
          onUpdateUser={fetchUserData}
        />
      )}
      
      {/* Auth page */}
      {!user && (
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={
                <Navigate to="/auth" replace />
              }
            />
            <Route
              path="/auth"
              element={<AuthPage onLogin={handleLogin} />}
            />
          </Routes>
        </BrowserRouter>
      )}
      
      <Toaster position="top-center" richColors />
    </div>
  );
}

function App() {
  return (
    <BattleModeProvider>
      <AppContent />
    </BattleModeProvider>
  );
}

export default App;
